import React from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  Sparkles,
  ImageIcon,
  Code2,
  Rocket,
  Target,
  ArrowRight,
  Gift,
  Percent,
} from "lucide-react";

const steps = [
  {
    icon: Lightbulb,
    label: "Idée & positionnement",
    desc: "On clarifie votre vision, votre cible, votre promesse.",
  },
  {
    icon: Sparkles,
    label: "Création de marque",
    desc: "Identité visuelle, logo, univers, ton de marque.",
  },
  {
    icon: ImageIcon,
    label: "Visuels & contenu",
    desc: "Visuels réseaux, publicités, supports print & digital.",
  },
  {
    icon: Code2,
    label: "Création du site",
    desc: "Site moderne, rapide, pensé pour convertir.",
  },
  {
    icon: Rocket,
    label: "Lancement",
    desc: "Stratégie de communication et mise en ligne orchestrée.",
  },
  {
    icon: Target,
    label: "Premiers clients",
    desc: "Acquisition ciblée pour générer vos premiers revenus.",
  },
];

export default function Accompagnement({ onOpenQuote }) {
  return (
    <section
      id="accompagnement"
      data-testid="accompagnement-section"
      className="relative py-24 md:py-32 section-light overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-14 md:mb-20"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Accompagnement complet
          </span>
          <h2 className="mt-4 font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A]">
            De l&rsquo;idée à{" "}
            <span className="gradient-text font-medium">
              vos premiers clients
            </span>
          </h2>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#E52431]/10 to-[#1F2C6E]/10 border border-[#E52431]/20">
            <Sparkles size={14} className="text-[#E52431]" />
            <span className="text-sm font-medium text-[#0A0A0A]">
              L&rsquo;offre la plus complète pour lancer votre activité de A à Z
            </span>
          </div>
          <p className="mt-6 text-[#0A0A0A]/65 text-lg font-light leading-relaxed">
            Nous accompagnons les entrepreneurs dès le départ jusqu&rsquo;à
            leurs premières ventes. Branding, visuels, site web, stratégie de
            communication et lancement&nbsp;: tout est pris en charge.
          </p>
          <p className="mt-4 text-[#0A0A0A]/55 text-sm font-light">
            Objectif&nbsp;: vous permettre de générer vos premiers revenus
            rapidement.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-[#0A0A0A]/10" />
          <div className="hidden lg:block absolute top-12 left-0 w-full h-px">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease: "easeOut" }}
              style={{ transformOrigin: "left" }}
              className="h-px w-full gradient-bg"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-4">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: i * 0.09 }}
                  data-testid={`accompagnement-step-${i}`}
                  className="relative group"
                >
                  <div className="flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0">
                    <div className="relative z-10 flex-shrink-0">
                      <div className="h-24 w-24 rounded-full bg-white border border-[#0A0A0A]/10 grid place-items-center group-hover:border-[#E52431]/60 group-hover:shadow-[0_20px_40px_-15px_rgba(229,36,49,0.25)] transition-all duration-500 relative">
                        <Icon
                          size={26}
                          className="text-[#0A0A0A]"
                          strokeWidth={1.5}
                        />
                        <span className="absolute -top-2 -right-2 h-8 w-8 rounded-full gradient-bg grid place-items-center text-xs font-semibold text-white">
                          {i + 1}
                        </span>
                      </div>
                    </div>

                    <div className="lg:mt-6 lg:text-center">
                      <h4 className="font-outfit text-base md:text-lg font-medium text-[#0A0A0A] leading-tight">
                        {s.label}
                      </h4>
                      <p className="mt-2 text-[#0A0A0A]/55 text-xs md:text-sm font-light leading-relaxed lg:px-2">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Pricing Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="accompagnement-pricing"
          className="mt-16 md:mt-20 relative rounded-3xl overflow-hidden"
        >
          <div
            className="relative p-8 md:p-12"
            style={{
              background:
                "linear-gradient(135deg, #0A0A0A 0%, #160812 35%, #0B1030 75%, #0A0A0A 100%)",
            }}
          >
            <div className="absolute -top-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#E52431]/25 blur-[100px]" />
            <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-[#1F2C6E]/35 blur-[100px]" />

            <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-center">
              {/* Price + Perks */}
              <div className="lg:col-span-7 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] font-semibold mb-5">
                  <Sparkles size={12} /> Formule complète
                </div>

                <div className="text-xs uppercase tracking-wider text-white/55">
                  À partir de
                </div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-outfit text-6xl md:text-7xl lg:text-8xl font-light tracking-tight text-white">
                    3490
                  </span>
                  <span className="text-3xl text-white/70 ml-1">€</span>
                  <span className="text-[11px] font-semibold tracking-[0.15em] text-white/60 ml-1">
                    TTC
                  </span>
                </div>

                <p className="mt-3 text-white/55 text-sm font-light italic">
                  Selon vos besoins et la complexité du projet.
                </p>

                <div className="mt-8 space-y-3">
                  <div className="flex items-start gap-3 p-4 rounded-2xl border border-[#E52431]/30 bg-gradient-to-r from-[#E52431]/10 to-[#1F2C6E]/10">
                    <Gift size={20} className="text-[#E52431] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-white">
                        Inclut le Pack Visibilité
                      </div>
                      <div className="text-xs text-white/70 font-light mt-0.5">
                        Valeur <span className="line-through opacity-70">2199 € TTC</span>{" "}
                        — intégrée à votre accompagnement
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-2xl border border-white/15 bg-white/5">
                    <Percent size={20} className="text-[#E52431] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-white">
                        Abonnement à tarif préférentiel
                      </div>
                      <div className="text-xs text-white/70 font-light mt-0.5">
                        Accès à nos formules mensuelles avec{" "}
                        <span className="text-[#E52431] font-semibold">-20%</span>{" "}
                        à vie après l&rsquo;accompagnement.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA side */}
              <div className="lg:col-span-5">
                <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-8">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/55 mb-2">
                    Prêt à être accompagné ?
                  </div>
                  <p className="text-white/80 font-light leading-relaxed">
                    Parlez-nous de votre projet. Nous revenons vers vous avec un
                    plan sur-mesure sous 24h.
                  </p>

                  <button
                    data-testid="accompagnement-cta"
                    onClick={onOpenQuote}
                    className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full gradient-bg text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
                  >
                    Être accompagné
                    <ArrowRight size={16} />
                  </button>
                  <p className="mt-3 text-center text-xs text-white/50 font-light">
                    ⚡ Réponse sous 24h · Devis personnalisé
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
